import axios from 'axios';
import { Employee, Location, Position, TimeStation, Schedule, PayrollDepartment, Role, Permission, BreakTimeSettings, SystemSetting, TrainingType, CompanySettings, PublicHoliday, CreateEmployeeRequest, UpdateEmployeeRequest, CreateLocationRequest, CreatePositionRequest, CreateTimeStationRequest, CreateScheduleRequest, CreatePayrollDepartmentRequest, CreateRoleRequest, UpdateRoleRequest, CreateBreakTimeSettingsRequest, CreateSystemSettingRequest, CreateTrainingTypeRequest, UpdateTrainingTypeRequest, CreateCompanySettingsRequest, UpdateCompanySettingsRequest, CreatePublicHolidayRequest, UpdatePublicHolidayRequest, EmployeeLicense, CreateEmployeeLicenseRequest, UpdateEmployeeLicenseRequest, PaymentType, PaymentFrequency, AssignLaborCostByOption, WorkingDay, PayRecord, CreatePayRecordRequest, UpdatePayRecordRequest, FeedbackRecord, CreateFeedbackRecordRequest, UpdateFeedbackRecordRequest, EmployeeNote, CreateEmployeeNoteRequest, UpdateEmployeeNoteRequest, TrainingRecord, CreateTrainingRecordRequest, UpdateTrainingRecordRequest, HolidayRequest, CreateHolidayRequestRequest, UpdateHolidayRequestRequest, BlockedDate, CreateBlockedDateRequest, UpdateBlockedDateRequest, Incident, CreateIncidentRequest, UpdateIncidentRequest, Shift, CreateShiftRequest, UpdateShiftRequest, ShiftTemplate, CreateShiftTemplateRequest, UpdateShiftTemplateRequest, AttendanceRecord, CreateAttendanceRecordRequest, UpdateAttendanceRecordRequest, Task, CreateTaskRequest, UpdateTaskRequest, TaskConversation, AddConversationRequest, TaskConversationReaction, SubTask, CreateSubTaskRequest, UpdateSubTaskRequest, SubTaskConversation, PayModel, CreatePayModelRequest, UpdatePayModelRequest, LeaveType, CreateLeaveTypeRequest, UpdateLeaveTypeRequest, LeavePolicy, CreateLeavePolicyRequest, UpdateLeavePolicyRequest, CreateLeavePolicyTypeRequest, UpdateLeavePolicyTypeRequest, EmployeeLeaveBalance, CreateEmployeeLeaveBalanceRequest, UpdateEmployeeLeaveBalanceRequest, EmployeeSickLeaveProfile, CreateEmployeeSickLeaveProfileRequest, UpdateEmployeeSickLeaveProfileRequest, SickLeaveRequest, CreateSickLeaveRequestRequest, UpdateSickLeaveRequestRequest, ApproveSickLeaveRequestRequest, RejectSickLeaveRequestRequest, EmployeePublicHoliday, CreateEmployeePublicHolidayRequest, UpdateEmployeePublicHolidayRequest, CheckEligibilityRequest, PublicHolidayPolicy, CreatePublicHolidayPolicyRequest, UpdatePublicHolidayPolicyRequest, RecurringJob, PublicHolidayJobRun, PublicHolidayJobLog, Job, CreateJobRequest, UpdateJobRequest, JobSchedule, CreateJobScheduleRequest, UpdateJobScheduleRequest, JobStep, CreateJobStepRequest, UpdateJobStepRequest, JobStepCondition, CreateJobStepConditionRequest, JobRunHistory, JobStepRunHistory, Timecard, CreateTimecardRequest, UpdateTimecardDayRequest, AuditLogConfig, AuditLogConfigFieldRequest, AuditLogEvent } from '../types';

// Runtime configuration - reads from window.APP_CONFIG (set in public/config.js)
// Falls back to environment variable, then localhost for development
const getApiBaseUrl = () => {
  // Check runtime config first (can be edited on server without rebuild)
  if (typeof window !== 'undefined' && (window as any).APP_CONFIG?.API_URL) {
    const url = (window as any).APP_CONFIG.API_URL;
    console.log('[API Config] Using runtime config:', url);
    return url;
  }
  // Fallback to environment variable (build-time)
  if (process.env.REACT_APP_API_URL) {
    const url = process.env.REACT_APP_API_URL;
    console.log('[API Config] Using environment variable:', url);
    return url;
  }
  // Default to localhost for development
  const url = 'http://localhost:5026/api';
  console.warn('[API Config] Using default localhost:', url);
  return url;
};

const API_BASE_URL = getApiBaseUrl();
console.log('[API] Base URL initialized:', API_BASE_URL);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add authentication token interceptor and handle FormData Content-Type
api.interceptors.request.use(
  (config) => {
    // Get auth token from localStorage
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // For FormData, remove Content-Type header so browser can set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: async (email: string, password: string): Promise<{ employee: Employee; token: string }> => {
    try {
      const response = await api.post('/auth/login', { email, password });
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || 'Login failed');
      }
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        throw new Error(`Cannot connect to API server. Please ensure the API server is running.`);
      }
      throw new Error(error?.message || 'Network error. Please check your connection.');
    }
  },

  logout: async (): Promise<void> => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('employee');
  },

  forgotPassword: {
    request: async (email: string): Promise<{ email: string; passwordQuestion: string; hasQuestion: boolean }> => {
      try {
        const response = await api.post('/forgot-password/request', { email });
        return response.data;
      } catch (error: any) {
        if (error?.response) {
          const message = error.response.data?.message || 'Failed to request password reset';
          throw new Error(message);
        } else if (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNREFUSED' || error?.message?.includes('Network Error')) {
          throw new Error('Network error. Cannot reach API server. Please check your connection.');
        } else {
          throw new Error(error?.message || 'Network error. Please check your connection.');
        }
      }
    },

    reset: async (email: string, passwordAnswer: string): Promise<{ message: string; emailSent: boolean }> => {
      try {
        const response = await api.post('/forgot-password/reset', { email, passwordAnswer });
        return response.data;
      } catch (error: any) {
        if (error.response) {
          throw new Error(error.response.data?.message || 'Failed to reset password');
        }
        throw new Error('Network error. Please check your connection.');
      }
    },
  },

  updateAccount: async (data: {
    email: string;
    newPassword?: string;
    passwordQuestion?: string;
    passwordAnswer?: string;
  }): Promise<{ message: string }> => {
    try {
      const response = await api.put('/auth/account', data);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        throw new Error(error.response.data?.message || 'Failed to update account');
      }
      throw new Error('Network error. Please check your connection.');
    }
  },
};

// Employee API
export const employeeApi = {
  getAll: async (): Promise<Employee[]> => {
    const response = await api.get('/employees');
    return response.data;
  },
  
  getById: async (id: number): Promise<Employee> => {
    const response = await api.get(`/employees/${id}`);
    return response.data;
  },
  
  create: async (employee: CreateEmployeeRequest): Promise<Employee> => {
    const response = await api.post('/employees', employee);
    return response.data;
  },
  
  update: async (id: number, employee: UpdateEmployeeRequest): Promise<void> => {
    await api.patch(`/employees/${id}`, employee);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employees/${id}`);
  },
};

// Location API
export const locationApi = {
  getAll: async (): Promise<Location[]> => {
    try {
      // Add cache-busting to ensure fresh data
      const response = await api.get('/locations', {
        params: { _t: Date.now() },
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      return response.data;
    } catch (error: any) {
      console.error('[Location API] Error:', error);
      console.error('[Location API] Error response:', error.response);
      console.error('[Location API] Error data:', error.response?.data);
      throw error;
    }
  },
  
  getById: async (id: number): Promise<Location> => {
    const response = await api.get(`/locations/${id}`);
    return response.data;
  },
  
  create: async (location: CreateLocationRequest): Promise<Location> => {
    const response = await api.post('/locations', location);
    return response.data;
  },
  
  update: async (id: number, location: CreateLocationRequest): Promise<void> => {
    const locationData = {
      id: id,
      name: location.name,
      address: location.address,
      timezone: location.timezone,
      latitude: location.latitude,
      longitude: location.longitude,
      isActive: true
    };
    await api.put(`/locations/${id}`, locationData);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/locations/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/locations/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/locations/${id}/activate`);
  },
};

// Position API
export const positionApi = {
  getAll: async (): Promise<Position[]> => {
    const response = await api.get('/positions');
    return response.data;
  },
  
  getById: async (id: number): Promise<Position> => {
    const response = await api.get(`/positions/${id}`);
    return response.data;
  },
  
  create: async (position: CreatePositionRequest): Promise<Position> => {
    const response = await api.post('/positions', position);
    return response.data;
  },
  
  update: async (id: number, position: CreatePositionRequest): Promise<void> => {
    const positionData = {
      id: id,
      title: position.title,
      description: position.description,
      color: position.color,
      isActive: true
    };
    await api.put(`/positions/${id}`, positionData);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/positions/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/positions/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/positions/${id}/activate`);
  },
};

// TimeStation API
export const timeStationApi = {
  getAll: async (): Promise<TimeStation[]> => {
    const response = await api.get('/timestations');
    return response.data;
  },
  
  getById: async (id: number): Promise<TimeStation> => {
    const response = await api.get(`/timestations/${id}`);
    return response.data;
  },
  
  create: async (timeStation: CreateTimeStationRequest): Promise<TimeStation> => {
    const response = await api.post('/timestations', timeStation);
    return response.data;
  },
  
  update: async (id: number, timeStation: CreateTimeStationRequest): Promise<void> => {
    const timeStationData: any = {
      name: timeStation.name,
      deviceId: timeStation.deviceId,
      description: timeStation.description,
      ipAddress: timeStation.ipAddress,
      locationId: timeStation.locationId,
      status: timeStation.status || (timeStation.isActive ? 'Active' : 'Inactive'),
      allowPhotoCapture: timeStation.allowPhotoCapture,
      locationTracking: timeStation.locationTracking,
      allowPinClockIn: timeStation.allowPinClockIn === true
    };
    // Always include allowedWifis (empty array if none)
    timeStationData.allowedWifis = timeStation.allowedWifis || [];
    // Always include allowedIpRanges (empty array if none)
    timeStationData.allowedIpRanges = timeStation.allowedIpRanges || [];
    await api.put(`/timestations/${id}`, timeStationData);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/timestations/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/timestations/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/timestations/${id}/activate`);
  },
  
  approve: async (id: number): Promise<void> => {
    await api.put(`/timestations/${id}/approve`);
  },
};

// Schedule API
export const scheduleApi = {
  getAll: async (): Promise<Schedule[]> => {
    const response = await api.get('/schedules');
    return response.data;
  },
  
  getById: async (id: number): Promise<Schedule> => {
    const response = await api.get(`/schedules/${id}`);
    return response.data;
  },
  
  create: async (schedule: CreateScheduleRequest): Promise<Schedule> => {
    const response = await api.post('/schedules', schedule);
    return response.data;
  },
  
  update: async (id: number, schedule: CreateScheduleRequest): Promise<Schedule> => {
    const response = await api.put(`/schedules/${id}`, schedule);
    return response.data;
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/schedules/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/schedules/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/schedules/${id}/activate`);
  },
};

export const payrollDepartmentApi = {
  getAll: async (): Promise<PayrollDepartment[]> => {
    const response = await api.get('/payrolldepartments');
    return response.data;
  },
  
  getById: async (id: number): Promise<PayrollDepartment> => {
    const response = await api.get(`/payrolldepartments/${id}`);
    return response.data;
  },
  
  create: async (department: CreatePayrollDepartmentRequest): Promise<PayrollDepartment> => {
    const response = await api.post('/payrolldepartments', department);
    return response.data;
  },
  
  update: async (id: number, department: CreatePayrollDepartmentRequest): Promise<void> => {
    await api.put(`/payrolldepartments/${id}`, department);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/payrolldepartments/${id}`);
  }
};

// Role API
export const roleApi = {
  getAll: async (): Promise<Role[]> => {
    const response = await api.get('/roles');
    return response.data;
  },
  
  getById: async (id: number): Promise<Role> => {
    const response = await api.get(`/roles/${id}`);
    return response.data;
  },
  
  getPermissions: async (): Promise<Permission[]> => {
    const response = await api.get('/roles/permissions');
    return response.data;
  },
  
  create: async (role: CreateRoleRequest): Promise<Role> => {
    const response = await api.post('/roles', role);
    return response.data;
  },
  
  update: async (id: number, role: UpdateRoleRequest): Promise<void> => {
    await api.put(`/roles/${id}`, role);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/roles/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/roles/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/roles/${id}/activate`);
  },
};

// Break Time Settings API
export const breakTimeSettingsApi = {
  get: async (): Promise<BreakTimeSettings> => {
    const response = await api.get('/break-time-settings');
    return response.data;
  },
  
  create: async (settings: CreateBreakTimeSettingsRequest): Promise<BreakTimeSettings> => {
    const response = await api.post('/break-time-settings', settings);
    return response.data;
  },
};

// System Settings API
export const systemSettingsApi = {
  getAll: async (): Promise<SystemSetting[]> => {
    const response = await api.get('/system-settings');
    return response.data;
  },
  
  getByKey: async (key: string): Promise<SystemSetting> => {
    const response = await api.get(`/system-settings/${key}`);
    return response.data;
  },
  
  create: async (setting: CreateSystemSettingRequest): Promise<SystemSetting> => {
    const response = await api.post('/system-settings', setting);
    return response.data;
  },
  
  update: async (key: string, value: string): Promise<void> => {
    await api.put(`/system-settings/${key}`, { value });
  },
};

// Training Types API
export const trainingTypeApi = {
  getAll: async (): Promise<TrainingType[]> => {
    const response = await api.get('/trainingtypes');
    return response.data;
  },
  
  getById: async (id: number): Promise<TrainingType> => {
    const response = await api.get(`/trainingtypes/${id}`);
    return response.data;
  },
  
  create: async (trainingType: CreateTrainingTypeRequest): Promise<TrainingType> => {
    const response = await api.post('/trainingtypes', trainingType);
    return response.data;
  },
  
  update: async (id: number, trainingType: UpdateTrainingTypeRequest): Promise<void> => {
    await api.put(`/trainingtypes/${id}`, trainingType);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/trainingtypes/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/trainingtypes/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/trainingtypes/${id}/activate`);
  },
};

// Company Settings API
export const companySettingsApi = {
  get: async (): Promise<CompanySettings> => {
    const response = await api.get('/companysettings');
    return response.data;
  },
  
  create: async (settings: CreateCompanySettingsRequest): Promise<CompanySettings> => {
    const response = await api.post('/companysettings', settings);
    return response.data;
  },
  
  update: async (id: number, settings: UpdateCompanySettingsRequest): Promise<void> => {
    await api.put(`/companysettings/${id}`, settings);
  },
};

// Public Holidays API
export const publicHolidayApi = {
  getAll: async (year?: number): Promise<PublicHoliday[]> => {
    const params = year ? { year } : {};
    const response = await api.get('/publicholidays', { params });
    return response.data;
  },
  
  getById: async (id: number): Promise<PublicHoliday> => {
    const response = await api.get(`/publicholidays/${id}`);
    return response.data;
  },
  
  create: async (holiday: CreatePublicHolidayRequest): Promise<PublicHoliday> => {
    const response = await api.post('/publicholidays', holiday);
    return response.data;
  },
  
  update: async (id: number, holiday: UpdatePublicHolidayRequest): Promise<void> => {
    await api.put(`/publicholidays/${id}`, holiday);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/publicholidays/${id}`);
  },
  
  deactivate: async (id: number): Promise<void> => {
    await api.put(`/publicholidays/${id}/deactivate`);
  },
  
  activate: async (id: number): Promise<void> => {
    await api.put(`/publicholidays/${id}/activate`);
  },
};

// Public Holiday Policy API
export const publicHolidayPolicyApi = {
  getAll: async (): Promise<PublicHolidayPolicy[]> => {
    const response = await api.get('/publicholidaypolicies');
    return response.data;
  },
  
  getById: async (id: number): Promise<PublicHolidayPolicy> => {
    const response = await api.get(`/publicholidaypolicies/${id}`);
    return response.data;
  },
  
  getActive: async (): Promise<PublicHolidayPolicy | null> => {
    const response = await api.get('/publicholidaypolicies/active');
    return response.data;
  },
  
  create: async (policy: CreatePublicHolidayPolicyRequest): Promise<PublicHolidayPolicy> => {
    const response = await api.post('/publicholidaypolicies', policy);
    return response.data;
  },
  
  update: async (id: number, policy: UpdatePublicHolidayPolicyRequest): Promise<void> => {
    await api.put(`/publicholidaypolicies/${id}`, policy);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/publicholidaypolicies/${id}`);
  },
};

// Employee License API
export const employeeLicenseApi = {
  getByEmployeeId: async (employeeId: number): Promise<EmployeeLicense[]> => {
    const response = await api.get(`/employee-licenses?employeeId=${employeeId}`);
    return response.data;
  },
  
  getById: async (id: number): Promise<EmployeeLicense> => {
    const response = await api.get(`/employee-licenses/${id}`);
    return response.data;
  },
  
  create: async (license: CreateEmployeeLicenseRequest): Promise<EmployeeLicense> => {
    const response = await api.post('/employee-licenses', license);
    return response.data;
  },
  
  update: async (id: number, license: UpdateEmployeeLicenseRequest): Promise<void> => {
    await api.put(`/employee-licenses/${id}`, license);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-licenses/${id}`);
  },
};

// Payment Type API
export const paymentTypeApi = {
  getAll: async (): Promise<PaymentType[]> => {
    const response = await api.get('/payment-types');
    return response.data;
  },
  
  getById: async (id: number): Promise<PaymentType> => {
    const response = await api.get(`/payment-types/${id}`);
    return response.data;
  },
};

// Payment Frequency API
export const paymentFrequencyApi = {
  getAll: async (): Promise<PaymentFrequency[]> => {
    const response = await api.get('/payment-frequencies');
    return response.data;
  },
  
  getById: async (id: number): Promise<PaymentFrequency> => {
    const response = await api.get(`/payment-frequencies/${id}`);
    return response.data;
  },
};

// Assign Labor Cost By Option API
export const assignLaborCostByOptionApi = {
  getAll: async (): Promise<AssignLaborCostByOption[]> => {
    const response = await api.get('/assign-labor-cost-by-options');
    return response.data;
  },
  
  getById: async (id: number): Promise<AssignLaborCostByOption> => {
    const response = await api.get(`/assign-labor-cost-by-options/${id}`);
    return response.data;
  },
};

// Working Day API
export const workingDayApi = {
  getAll: async (): Promise<WorkingDay[]> => {
    const response = await api.get('/working-days');
    return response.data;
  },
  
  getById: async (id: number): Promise<WorkingDay> => {
    const response = await api.get(`/working-days/${id}`);
    return response.data;
  },
};

// Pay Record API
export const payRecordApi = {
  getAll: async (): Promise<PayRecord[]> => {
    const response = await api.get('/pay-records');
    return response.data;
  },
  
  getByEmployeeId: async (employeeId: number): Promise<PayRecord[]> => {
    const response = await api.get(`/pay-records?employeeId=${employeeId}`);
    return response.data;
  },
  
  getById: async (id: number): Promise<PayRecord> => {
    const response = await api.get(`/pay-records/${id}`);
    return response.data;
  },
  
  create: async (payRecord: CreatePayRecordRequest): Promise<PayRecord> => {
    const response = await api.post('/pay-records', payRecord);
    return response.data;
  },
  
  update: async (id: number, payRecord: UpdatePayRecordRequest): Promise<void> => {
    await api.put(`/pay-records/${id}`, payRecord);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/pay-records/${id}`);
  },
};

// Feedback Record API
export const feedbackRecordApi = {
  getByEmployeeId: async (employeeId: number): Promise<FeedbackRecord[]> => {
    const response = await api.get(`/feedback-records?employeeId=${employeeId}`);
    return response.data;
  },
  
  getById: async (id: number): Promise<FeedbackRecord> => {
    const response = await api.get(`/feedback-records/${id}`);
    return response.data;
  },
  
  create: async (feedbackRecord: CreateFeedbackRecordRequest): Promise<FeedbackRecord> => {
    const response = await api.post('/feedback-records', feedbackRecord);
    return response.data;
  },
  
  update: async (id: number, feedbackRecord: UpdateFeedbackRecordRequest): Promise<void> => {
    await api.put(`/feedback-records/${id}`, feedbackRecord);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/feedback-records/${id}`);
  },
};

// Employee Note API
export const employeeNoteApi = {
  getByEmployeeId: async (employeeId: number): Promise<EmployeeNote[]> => {
    const response = await api.get(`/employee-notes?employeeId=${employeeId}`);
    return response.data;
  },
  
  getById: async (id: number): Promise<EmployeeNote> => {
    const response = await api.get(`/employee-notes/${id}`);
    return response.data;
  },
  
  create: async (employeeNote: CreateEmployeeNoteRequest): Promise<EmployeeNote> => {
    const response = await api.post('/employee-notes', employeeNote);
    return response.data;
  },
  
  update: async (id: number, employeeNote: UpdateEmployeeNoteRequest): Promise<void> => {
    await api.put(`/employee-notes/${id}`, employeeNote);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-notes/${id}`);
  },
};

// Training Record API
export const trainingRecordApi = {
  getByEmployeeId: async (employeeId: number): Promise<TrainingRecord[]> => {
    const response = await api.get(`/training-records?employeeId=${employeeId}`);
    return response.data;
  },
  
  getById: async (id: number): Promise<TrainingRecord> => {
    const response = await api.get(`/training-records/${id}`);
    return response.data;
  },
  
  create: async (trainingRecord: CreateTrainingRecordRequest): Promise<TrainingRecord> => {
    const response = await api.post('/training-records', trainingRecord);
    return response.data;
  },
  
  update: async (id: number, trainingRecord: UpdateTrainingRecordRequest): Promise<void> => {
    await api.put(`/training-records/${id}`, trainingRecord);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/training-records/${id}`);
  },
};

// Holiday Request API
export const holidayRequestApi = {
  getAll: async (employeeId?: number): Promise<HolidayRequest[]> => {
    const url = employeeId ? `/holiday-requests?employeeId=${employeeId}` : '/holiday-requests';
    const response = await api.get(url);
    return response.data;
  },
  
  getById: async (id: number): Promise<HolidayRequest> => {
    const response = await api.get(`/holiday-requests/${id}`);
    return response.data;
  },
  
  create: async (holidayRequest: CreateHolidayRequestRequest): Promise<HolidayRequest> => {
    const response = await api.post('/holiday-requests', holidayRequest);
    return response.data;
  },
  
  update: async (id: number, holidayRequest: UpdateHolidayRequestRequest): Promise<void> => {
    await api.put(`/holiday-requests/${id}`, holidayRequest);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/holiday-requests/${id}`);
  },
  
  approve: async (id: number): Promise<void> => {
    await api.put(`/holiday-requests/${id}/approve`);
  },
  
  reject: async (id: number, rejectionReason?: string): Promise<void> => {
    await api.put(`/holiday-requests/${id}/reject`, { rejectionReason });
  },
};

// Blocked Dates API
export const blockedDateApi = {
  getAll: async (): Promise<BlockedDate[]> => {
    const response = await api.get('/blocked-dates');
    return response.data;
  },

  create: async (blockedDate: CreateBlockedDateRequest): Promise<BlockedDate> => {
    const response = await api.post('/blocked-dates', blockedDate);
    return response.data;
  },

  update: async (id: number, blockedDate: UpdateBlockedDateRequest): Promise<void> => {
    await api.put(`/blocked-dates/${id}`, blockedDate);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/blocked-dates/${id}`);
  },
};

// Incidents API
export const incidentApi = {
  getAll: async (employeeId?: number): Promise<Incident[]> => {
    const params = employeeId ? { employeeId } : {};
    const response = await api.get('/incidents', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Incident> => {
    const response = await api.get(`/incidents/${id}`);
    return response.data;
  },

  create: async (incident: CreateIncidentRequest): Promise<Incident> => {
    const response = await api.post('/incidents', incident);
    return response.data;
  },

  update: async (id: number, incident: UpdateIncidentRequest): Promise<void> => {
    await api.put(`/incidents/${id}`, incident);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/incidents/${id}`);
  },
};

// Shifts API
export const shiftApi = {
  getAll: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
    scheduleId?: number;
    employeeId?: number;
  }): Promise<Shift[]> => {
    const response = await api.get('/shifts', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Shift> => {
    const response = await api.get(`/shifts/${id}`);
    return response.data;
  },

  create: async (shift: CreateShiftRequest): Promise<Shift> => {
    const response = await api.post('/shifts', shift);
    return response.data;
  },

  update: async (id: number, shift: UpdateShiftRequest): Promise<void> => {
    await api.patch(`/shifts/${id}`, shift);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/shifts/${id}`);
  },

  deleteWeek: async (weekStart: string): Promise<{ deletedCount: number }> => {
    const response = await api.delete(`/shifts/week/${weekStart}`);
    return response.data;
  },

  getCounts: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
  }): Promise<Array<{ scheduleId: number; totalShifts: number; uniqueEmployees: number; totalEmployeesAssigned: number }>> => {
    const response = await api.get('/shifts/counts', { params });
    return response.data;
  },
};

// Shift Templates API
export const shiftTemplateApi = {
  getAll: async (locationId?: number): Promise<ShiftTemplate[]> => {
    const params = locationId ? { locationId } : {};
    const response = await api.get('/shift-templates', { params });
    return response.data;
  },

  getById: async (id: number): Promise<ShiftTemplate> => {
    const response = await api.get(`/shift-templates/${id}`);
    return response.data;
  },

  create: async (template: CreateShiftTemplateRequest): Promise<ShiftTemplate> => {
    const response = await api.post('/shift-templates', template);
    return response.data;
  },

  update: async (id: number, template: UpdateShiftTemplateRequest): Promise<void> => {
    await api.put(`/shift-templates/${id}`, template);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/shift-templates/${id}`);
  },
};

// Employee Schedule Sort Order API
export const employeeSortOrderApi = {
  get: async (locationId: number, scheduleId: number, weekStartDate: string): Promise<{ employeeId: number; sortOrder: number }[]> => {
    const response = await api.get('/employee-schedule-sort-order', {
      params: { locationId, scheduleId, weekStartDate }
    });
    return response.data;
  },
  
  save: async (locationId: number, scheduleId: number, weekStartDate: string, sortOrders: { employeeId: number; sortOrder: number }[]): Promise<void> => {
    await api.post('/employee-schedule-sort-order', {
      locationId,
      scheduleId,
      weekStartDate,
      sortOrders
    });
  },
};

export const schedulePublicationStatusApi = {
  get: async (locationId: number, scheduleId: number, weekStartDate: string): Promise<{ status: string; id?: number; publishedAt?: string; changedAt?: string }> => {
    const response = await api.get('/schedule-publication-status', {
      params: { locationId, scheduleId, weekStartDate }
    });
    return response.data;
  },
  publish: async (locationId: number, scheduleId: number, weekStartDate: string): Promise<{ status: string; id: number; publishedAt: string }> => {
    const response = await api.post('/schedule-publication-status/publish', {
      locationId,
      scheduleId,
      weekStartDate
    });
    return response.data;
  },
  unpublish: async (locationId: number, scheduleId: number, weekStartDate: string): Promise<{ status: string }> => {
    const response = await api.post('/schedule-publication-status/unpublish', {
      locationId,
      scheduleId,
      weekStartDate
    });
    return response.data;
  },
  markChanged: async (locationId: number, scheduleId: number, weekStartDate: string): Promise<{ status: string; id: number; changedAt: string }> => {
    const response = await api.post('/schedule-publication-status/mark-changed', {
      locationId,
      scheduleId,
      weekStartDate
    });
    return response.data;
  },
};

// Attendance API
export const attendanceApi = {
  getAll: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
    employeeId?: number;
  }): Promise<AttendanceRecord[]> => {
    const response = await api.get('/attendance', { params });
    return response.data;
  },

  getCounts: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
    employeeId?: number;
  }): Promise<Array<{ date: string; count: number }>> => {
    const response = await api.get('/attendance/counts', { params });
    return response.data;
  },

  getHistory: async (params?: {
    startDate?: string;
    endDate?: string;
    locationId?: number;
    employeeId?: number;
  }): Promise<{ data: AttendanceRecord[]; pagination?: any }> => {
    const response = await api.get('/attendance/history', { params });
    return response.data;
  },

  getById: async (id: number): Promise<AttendanceRecord> => {
    const response = await api.get(`/attendance/${id}`);
    return response.data;
  },

  create: async (attendance: CreateAttendanceRecordRequest): Promise<AttendanceRecord> => {
    const response = await api.post('/attendance/manual', attendance);
    return response.data;
  },

  update: async (id: number, attendance: UpdateAttendanceRecordRequest): Promise<void> => {
    await api.patch(`/attendance/${id}`, attendance);
  },

  delete: async (id: number, isHardDelete?: boolean): Promise<void> => {
    await api.delete(`/attendance/${id}`, {
      params: isHardDelete ? { isHardDelete: true } : {}
    });
  },

  clockIn: async (employeeId: number, imageBase64?: string, timeStationId?: number): Promise<{ success: boolean; message: string; attendanceRecordId: number; clockInTime: string; faceMatchConfidence?: number }> => {
    const response = await api.post('/attendance/clock-in', {
      employeeId,
      imageBase64,
      timeStationId
    });
    return response.data;
  },

  clockOut: async (employeeId: number, imageBase64?: string, timeStationId?: number): Promise<{ success: boolean; message: string; attendanceRecordId: number; clockInTime: string; clockOutTime: string; faceMatchConfidence?: number }> => {
    const response = await api.post('/attendance/clock-out', {
      employeeId,
      imageBase64,
      timeStationId
    });
    return response.data;
  },
};

// Face Recognition API
export const faceApi = {
  getSyncStatus: async (): Promise<{
    databaseFaceCount: number;
    rekognitionFaceCount: number;
    isInSync: boolean;
    orphanedFacesCount: number;
    missingFacesCount: number;
    orphanedFaceIds: string[];
    missingFaceIds: string[];
    rekognitionAvailable?: boolean;
    rekognitionErrorMessage?: string;
  }> => {
    const response = await api.get('/face-recognition/sync-status');
    return response.data;
  },

  syncFaces: async (): Promise<{
    success: boolean;
    databaseFaceCount: number;
    rekognitionFaceCountBefore: number;
    rekognitionFaceCountAfter: number;
    orphanedFacesDeleted: number;
    missingFacesCleared: number;
    message: string;
  }> => {
    const response = await api.post('/face-recognition/sync');
    return response.data;
  },
};

// Task API
export const taskApi = {
  getAll: async (assignedToEmployeeId?: number): Promise<Task[]> => {
    const params = assignedToEmployeeId ? { assignedToEmployeeId } : {};
    const response = await api.get('/tasks', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Task> => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },

  create: async (task: CreateTaskRequest | FormData): Promise<Task> => {
    const config = task instanceof FormData 
      ? {} 
      : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.post('/tasks', task, config);
    return response.data;
  },

  update: async (id: number, task: UpdateTaskRequest | FormData): Promise<Task> => {
    const config = task instanceof FormData 
      ? {} 
      : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.put(`/tasks/${id}`, task, config);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  },

  getConversations: async (taskId: number): Promise<TaskConversation[]> => {
    const response = await api.get(`/tasks/${taskId}/conversations`);
    return response.data;
  },

  addConversation: async (taskId: number, request: AddConversationRequest | FormData): Promise<TaskConversation> => {
    console.log('[API] addConversation called');
    console.log('[API] Task ID:', taskId);
    console.log('[API] Request type:', request instanceof FormData ? 'FormData' : 'JSON');
    
    if (request instanceof FormData) {
      console.log('[API] FormData entries:');
      const entries = Array.from(request.entries());
      entries.forEach(([key, value]) => {
        if (value && typeof value === 'object' && 'name' in value && 'size' in value && 'type' in value) {
          const file = value as File;
          console.log(`[API]   ${key}: File(${file.name}, ${file.size} bytes, ${file.type})`);
        } else {
          console.log(`[API]   ${key}: ${value}`);
        }
      });
    } else {
      console.log('[API] JSON request:', request);
    }
    
    // For FormData, create a request without the default Content-Type header
    // For JSON, explicitly set Content-Type
    try {
      // The interceptor will handle removing Content-Type for FormData
      // For JSON, explicitly set Content-Type
      const config = request instanceof FormData 
        ? {} // Let interceptor handle FormData
        : { headers: { 'Content-Type': 'application/json' } };
      
      console.log('[API] Making POST request:', request instanceof FormData ? 'FormData' : 'JSON');
      const response = await api.post(`/tasks/${taskId}/conversations`, request, config);
      
      console.log('[API] ✅ Response received:', response);
      console.log('[API] Response status:', response.status);
      console.log('[API] Response data:', response.data);
      console.log('[API] Response data type:', typeof response.data);
      console.log('[API] Response data attachments:', response.data.attachments);
      console.log('[API] Response data attachments type:', typeof response.data.attachments);
      console.log('[API] Response data attachments is array:', Array.isArray(response.data.attachments));
      console.log('[API] Response data attachments count:', response.data.attachments?.length || 0);
      
      if (response.data.attachments && Array.isArray(response.data.attachments)) {
        response.data.attachments.forEach((att: any, idx: number) => {
          console.log(`[API]   Attachment ${idx + 1} in response:`, att);
        });
      } else {
        console.warn('[API] ⚠️ No attachments array in response data!');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('[API] ❌ Error in addConversation:', error);
      console.error('[API] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        request: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      if (error.response?.data) {
        console.error('[API] Full error response data:', JSON.stringify(error.response.data, null, 2));
        console.error('[API] Error response message:', error.response.data.message);
        console.error('[API] Error response error:', error.response.data.error);
      } else {
        console.error('[API] No response data in error');
      }
      throw error;
    }
  },
  addReaction: async (taskId: number, conversationId: number, reactionType: string): Promise<{ reactions: TaskConversationReaction[] }> => {
    const response = await api.post(`/tasks/${taskId}/conversations/${conversationId}/reactions`, { ReactionType: reactionType });
    return response.data;
  },
  removeReaction: async (taskId: number, conversationId: number): Promise<{ reactions: TaskConversationReaction[] }> => {
    const response = await api.delete(`/tasks/${taskId}/conversations/${conversationId}/reactions`);
    return response.data;
  },
  deleteAttachment: async (taskId: number, attachmentId: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}/attachments/${attachmentId}`);
  },
};

// SubTask API
export const subTaskApi = {
  getAll: async (taskId: number): Promise<SubTask[]> => {
    const response = await api.get(`/tasks/${taskId}/subtasks`);
    return response.data;
  },

  getById: async (taskId: number, id: number): Promise<SubTask> => {
    const response = await api.get(`/tasks/${taskId}/subtasks/${id}`);
    return response.data;
  },

  create: async (taskId: number, subTask: CreateSubTaskRequest | FormData): Promise<SubTask> => {
    const config = subTask instanceof FormData 
      ? {} 
      : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.post(`/tasks/${taskId}/subtasks`, subTask, config);
    return response.data;
  },

  update: async (taskId: number, id: number, subTask: UpdateSubTaskRequest | FormData): Promise<SubTask> => {
    const config = subTask instanceof FormData 
      ? {} 
      : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.put(`/tasks/${taskId}/subtasks/${id}`, subTask, config);
    return response.data;
  },

  delete: async (taskId: number, id: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}/subtasks/${id}`);
  },

  getConversations: async (taskId: number, subTaskId: number): Promise<SubTaskConversation[]> => {
    const response = await api.get(`/tasks/${taskId}/subtasks/${subTaskId}/conversations`);
    return response.data;
  },

  addConversation: async (taskId: number, subTaskId: number, request: AddConversationRequest | FormData): Promise<SubTaskConversation> => {
    const config = request instanceof FormData 
      ? {} 
      : { headers: { 'Content-Type': 'application/json' } };
    const response = await api.post(`/tasks/${taskId}/subtasks/${subTaskId}/conversations`, request, config);
    return response.data;
  },

  addReaction: async (taskId: number, subTaskId: number, conversationId: number, reactionType: string): Promise<{ reactions: TaskConversationReaction[] }> => {
    const response = await api.post(`/tasks/${taskId}/subtasks/${subTaskId}/conversations/${conversationId}/reactions`, { ReactionType: reactionType });
    return response.data;
  },

  removeReaction: async (taskId: number, subTaskId: number, conversationId: number): Promise<{ reactions: TaskConversationReaction[] }> => {
    const response = await api.delete(`/tasks/${taskId}/subtasks/${subTaskId}/conversations/${conversationId}/reactions`);
    return response.data;
  },
  deleteAttachment: async (taskId: number, subTaskId: number, attachmentId: number): Promise<void> => {
    await api.delete(`/tasks/${taskId}/subtasks/${subTaskId}/attachments/${attachmentId}`);
  },
};

// ============================================
// LEAVE MANAGEMENT APIs
// ============================================

// PayModel API
export const payModelApi = {
  getAll: async (): Promise<PayModel[]> => {
    const response = await api.get('/paymodels');
    return response.data;
  },
  
  getById: async (id: number): Promise<PayModel> => {
    const response = await api.get(`/paymodels/${id}`);
    return response.data;
  },
  
  create: async (payModel: CreatePayModelRequest): Promise<PayModel> => {
    const response = await api.post('/paymodels', payModel);
    return response.data;
  },
  
  update: async (id: number, payModel: UpdatePayModelRequest): Promise<void> => {
    await api.put(`/paymodels/${id}`, payModel);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/paymodels/${id}`);
  },
};

// LeaveType API
export const leaveTypeApi = {
  getAll: async (): Promise<LeaveType[]> => {
    const response = await api.get('/leavetypes');
    return response.data;
  },
  
  getById: async (id: number): Promise<LeaveType> => {
    const response = await api.get(`/leavetypes/${id}`);
    return response.data;
  },
  
  create: async (leaveType: CreateLeaveTypeRequest): Promise<LeaveType> => {
    const response = await api.post('/leavetypes', leaveType);
    return response.data;
  },
  
  update: async (id: number, leaveType: UpdateLeaveTypeRequest): Promise<void> => {
    await api.put(`/leavetypes/${id}`, leaveType);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/leavetypes/${id}`);
  },
};

// Employee Leave Balance API
export const employeeLeaveBalanceApi = {
  getAll: async (employeeId?: number, leaveYear?: number): Promise<EmployeeLeaveBalance[]> => {
    const params: any = {};
    if (employeeId) params.employeeId = employeeId;
    if (leaveYear) params.leaveYear = leaveYear;
    const response = await api.get('/employee-leave-balances', { params });
    return response.data;
  },
  
  getById: async (id: number): Promise<EmployeeLeaveBalance> => {
    const response = await api.get(`/employee-leave-balances/${id}`);
    return response.data;
  },
  
  create: async (balance: CreateEmployeeLeaveBalanceRequest): Promise<EmployeeLeaveBalance> => {
    const response = await api.post('/employee-leave-balances', balance);
    return response.data;
  },
  
  update: async (id: number, balance: UpdateEmployeeLeaveBalanceRequest): Promise<void> => {
    await api.put(`/employee-leave-balances/${id}`, balance);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-leave-balances/${id}`);
  },
};

// Leave Policies API
export const leavePoliciesApi = {
  getAll: async (): Promise<LeavePolicy[]> => {
    const response = await api.get('/leavepolicies');
    return response.data;
  },
  
  getById: async (id: number): Promise<LeavePolicy> => {
    const response = await api.get(`/leavepolicies/${id}`);
    return response.data;
  },
  
  create: async (policy: CreateLeavePolicyRequest): Promise<LeavePolicy> => {
    const response = await api.post('/leavepolicies', policy);
    return response.data;
  },
  
  update: async (id: number, policy: UpdateLeavePolicyRequest): Promise<void> => {
    await api.put(`/leavepolicies/${id}`, policy);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/leavepolicies/${id}`);
  },
  
  addLeaveType: async (policyId: number, leaveType: CreateLeavePolicyTypeRequest): Promise<{ leavePolicyTypeId: number; message: string }> => {
    const response = await api.post(`/leavepolicies/${policyId}/leavetypes`, leaveType);
    return response.data;
  },
  
  updateLeaveType: async (policyId: number, leaveTypeId: number, leaveType: UpdateLeavePolicyTypeRequest): Promise<void> => {
    await api.put(`/leavepolicies/${policyId}/leavetypes/${leaveTypeId}`, leaveType);
  },
  
  removeLeaveType: async (policyId: number, leaveTypeId: number): Promise<void> => {
    await api.delete(`/leavepolicies/${policyId}/leavetypes/${leaveTypeId}`);
  },
};

// Employee Sick Leave Profile API
export const employeeSickLeaveProfileApi = {
  getAll: async (employeeId?: number): Promise<EmployeeSickLeaveProfile[]> => {
    const params = employeeId ? { employeeId } : {};
    const response = await api.get('/employee-sick-leave-profiles', { params });
    return response.data;
  },
  
  getById: async (id: number): Promise<EmployeeSickLeaveProfile> => {
    const response = await api.get(`/employee-sick-leave-profiles/${id}`);
    return response.data;
  },
  
  getByEmployeeId: async (employeeId: number): Promise<EmployeeSickLeaveProfile> => {
    const response = await api.get(`/employee-sick-leave-profiles/employee/${employeeId}`);
    return response.data;
  },
  
  create: async (profile: CreateEmployeeSickLeaveProfileRequest): Promise<EmployeeSickLeaveProfile> => {
    const response = await api.post('/employee-sick-leave-profiles', profile);
    return response.data;
  },
  
  update: async (id: number, profile: UpdateEmployeeSickLeaveProfileRequest): Promise<void> => {
    await api.put(`/employee-sick-leave-profiles/${id}`, profile);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-sick-leave-profiles/${id}`);
  },
};

// Sick Leave Request API
export const sickLeaveRequestApi = {
  getAll: async (employeeId?: number, status?: string): Promise<SickLeaveRequest[]> => {
    const params: any = {};
    if (employeeId) params.employeeId = employeeId;
    if (status) params.status = status;
    const response = await api.get('/sick-leave-requests', { params });
    return response.data;
  },
  
  getById: async (id: number): Promise<SickLeaveRequest> => {
    const response = await api.get(`/sick-leave-requests/${id}`);
    return response.data;
  },
  
  create: async (request: CreateSickLeaveRequestRequest): Promise<SickLeaveRequest> => {
    const response = await api.post('/sick-leave-requests', request);
    return response.data;
  },
  
  update: async (id: number, request: UpdateSickLeaveRequestRequest): Promise<void> => {
    await api.put(`/sick-leave-requests/${id}`, request);
  },
  
  approve: async (id: number, request?: ApproveSickLeaveRequestRequest): Promise<void> => {
    await api.put(`/sick-leave-requests/${id}/approve`, request || {});
  },
  
  reject: async (id: number, request: RejectSickLeaveRequestRequest): Promise<void> => {
    await api.put(`/sick-leave-requests/${id}/reject`, request);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/sick-leave-requests/${id}`);
  },
};

// Jobs API
export const jobsApi = {
  getAll: async (): Promise<Job[]> => {
    const response = await api.get('/jobs');
    return response.data;
  },

  getById: async (id: number): Promise<Job> => {
    const response = await api.get(`/jobs/${id}`);
    return response.data;
  },

  create: async (job: CreateJobRequest): Promise<Job> => {
    const response = await api.post('/jobs', job);
    return response.data;
  },

  update: async (id: number, job: UpdateJobRequest): Promise<void> => {
    await api.put(`/jobs/${id}`, job);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/jobs/${id}`);
  },
};

// Job Schedules API
export const jobSchedulesApi = {
  getAll: async (jobId: number): Promise<JobSchedule[]> => {
    const response = await api.get(`/jobs/${jobId}/schedules`);
    return response.data;
  },

  getById: async (jobId: number, scheduleId: number): Promise<JobSchedule> => {
    const response = await api.get(`/jobs/${jobId}/schedules/${scheduleId}`);
    return response.data;
  },

  create: async (jobId: number, schedule: CreateJobScheduleRequest): Promise<JobSchedule> => {
    const response = await api.post(`/jobs/${jobId}/schedules`, schedule);
    return response.data;
  },

  update: async (jobId: number, scheduleId: number, schedule: UpdateJobScheduleRequest): Promise<void> => {
    await api.put(`/jobs/${jobId}/schedules/${scheduleId}`, schedule);
  },

  delete: async (jobId: number, scheduleId: number): Promise<void> => {
    await api.delete(`/jobs/${jobId}/schedules/${scheduleId}`);
  },
};

// Job Steps API
export const jobStepsApi = {
  getAll: async (jobId: number): Promise<JobStep[]> => {
    const response = await api.get(`/jobs/${jobId}/steps`);
    return response.data;
  },

  getById: async (jobId: number, stepId: number): Promise<JobStep> => {
    const response = await api.get(`/jobs/${jobId}/steps/${stepId}`);
    return response.data;
  },

  create: async (jobId: number, step: CreateJobStepRequest): Promise<JobStep> => {
    const response = await api.post(`/jobs/${jobId}/steps`, step);
    return response.data;
  },

  update: async (jobId: number, stepId: number, step: UpdateJobStepRequest): Promise<void> => {
    await api.put(`/jobs/${jobId}/steps/${stepId}`, step);
  },

  delete: async (jobId: number, stepId: number): Promise<void> => {
    await api.delete(`/jobs/${jobId}/steps/${stepId}`);
  },

  execute: async (jobId: number, stepId: number): Promise<{ message: string }> => {
    const response = await api.post(`/jobs/${jobId}/steps/${stepId}/execute`);
    return response.data;
  },
};

// Job Monitor API
export const jobMonitorApi = {
  getRecurringJobs: async (): Promise<RecurringJob[]> => {
    const response = await api.get('/job-monitor/recurring-jobs');
    return response.data;
  },

  triggerJob: async (jobId: string): Promise<{ message: string }> => {
    const response = await api.post(`/job-monitor/trigger/${jobId}`);
    return response.data;
  },

  getJobRuns: async (holidayId?: number, page: number = 1, pageSize: number = 20): Promise<{
    data: PublicHolidayJobRun[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const params: any = { page, pageSize };
    if (holidayId) params.holidayId = holidayId;
    const response = await api.get('/job-monitor/job-runs', { params });
    return response.data;
  },

  getJobLogs: async (jobRunId: number): Promise<PublicHolidayJobLog[]> => {
    const response = await api.get(`/job-monitor/job-runs/${jobRunId}/logs`);
    return response.data;
  },

  getJobRunHistory: async (jobId?: number, page: number = 1, pageSize: number = 20): Promise<{
    data: JobRunHistory[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const params: any = { page, pageSize };
    if (jobId) params.jobId = jobId;
    const response = await api.get('/job-monitor/job-run-history', { params });
    return response.data;
  },

  getJobStepRunHistory: async (jobRunHistoryId: number): Promise<JobStepRunHistory[]> => {
    const response = await api.get(`/job-monitor/job-run-history/${jobRunHistoryId}/step-runs`);
    return response.data;
  },
};

// Employee Public Holiday API
export const employeePublicHolidayApi = {
  getAll: async (employeeId?: number, holidayId?: number, year?: number): Promise<EmployeePublicHoliday[]> => {
    const params: any = {};
    if (employeeId) params.employeeId = employeeId;
    if (holidayId) params.holidayId = holidayId;
    if (year) params.year = year;
    const response = await api.get('/employee-public-holidays', { params });
    return response.data;
  },
  
  getById: async (id: number): Promise<EmployeePublicHoliday> => {
    const response = await api.get(`/employee-public-holidays/${id}`);
    return response.data;
  },
  
  checkEligibility: async (request: CheckEligibilityRequest): Promise<any> => {
    const response = await api.post('/employee-public-holidays/check-eligibility', request);
    return response.data;
  },
  
  create: async (request: CreateEmployeePublicHolidayRequest): Promise<EmployeePublicHoliday> => {
    const response = await api.post('/employee-public-holidays', request);
    return response.data;
  },
  
  update: async (id: number, request: UpdateEmployeePublicHolidayRequest): Promise<void> => {
    await api.put(`/employee-public-holidays/${id}`, request);
  },
  
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employee-public-holidays/${id}`);
  },
};

// Timecard API
export const timecardApi = {
  getAll: async (employeeId?: number, weekStartDate?: string): Promise<Timecard[]> => {
    const params: any = {};
    if (employeeId) params.employeeId = employeeId;
    if (weekStartDate) params.weekStartDate = weekStartDate;
    const response = await api.get('/timecards', { params });
    return response.data;
  },
  
  getByEmployeeAndWeek: async (employeeId: number, weekStartDate: string, locationId?: number): Promise<Timecard | null> => {
    try {
      const params: Record<string, number> = {};
      if (locationId !== undefined && locationId !== null && locationId !== 0) {
        params.locationId = locationId;
      }
      const response = await api.get(`/timecards/employee/${employeeId}/week/${weekStartDate}`, { params });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },
  
  create: async (request: CreateTimecardRequest): Promise<Timecard> => {
    const response = await api.post('/timecards', request);
    return response.data;
  },
  
  addDay: async (timecardId: number, request: { date: string; mode?: string; extras?: number; totalHours?: number }): Promise<{ id: number; date: string; mode: string }> => {
    const response = await api.post(`/timecards/${timecardId}/day`, request);
    return response.data;
  },
  
  updateDay: async (timecardId: number, dayId: number, request: UpdateTimecardDayRequest): Promise<void> => {
    await api.put(`/timecards/${timecardId}/day/${dayId}`, request);
  },

  deleteDay: async (timecardId: number, dayId: number): Promise<void> => {
    await api.delete(`/timecards/${timecardId}/day/${dayId}`);
  },
  
  sync: async (employeeId: number, weekStartDate: string, locationId?: number): Promise<Timecard> => {
    const body: any = { employeeId, weekStartDate };
    if (locationId !== undefined && locationId !== null && locationId !== 0) {
      body.locationId = locationId;
    }
    const response = await api.post('/timecards/sync', body);
    return response.data;
  },
  
  syncStep1: async (employeeId: number, weekStartDate: string, locationId?: number): Promise<{ 
    success: boolean; 
    message: string; 
    shifts: Array<{ shiftId: number; date: string; inTime: string; outTime: string; breakMins: number; breakPaid: boolean }>;
    attendanceRecords: Array<{ attendanceId: number; date: string; inTime: string; outTime: string | null; adjustedInTime: string | null; adjustedOutTime: string | null }>;
    step3Records: Array<{ attendanceId: number; date: string; inTime: string; outTime: string | null }>;
    step4Records: Array<{ shiftId: number; shiftDate: string; shiftInTime: string; shiftOutTime: string; shiftBreakMins: number; shiftBreakPaid: boolean; actualInTime: string | null; actualOutTime: string | null; actualInDate: string; actualOutDate: string; actualBreakMins: number | null; matchedAttendanceCount: number }>;
    step5Records: Array<{ shiftId: number | null; shiftDate: string | null; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualInDate: string; actualOutDate: string; actualBreakMins: number | null; matchedAttendanceCount: number }>;
    step7Records: Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string }>;
    step8Records: Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string }>;
    step9Records: Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string; paidInTime: string | null; paidOutTime: string | null; paidBreakMins: number | null; paidBreakPaid: boolean | null }>;
    step10Records: Array<{ date: string; shiftInTime: string | null; shiftOutTime: string | null; shiftBreakMins: number | null; shiftBreakPaid: boolean | null; actualInTime: string | null; actualOutTime: string | null; actualBreakMins: number | null; hash: string; matchType: string; paidInTime: string | null; paidOutTime: string | null; paidBreakMins: number | null; paidBreakPaid: boolean | null; status: string }>;
  }> => {
    const body: any = { employeeId, weekStartDate };
    if (locationId !== undefined && locationId !== null && locationId !== 0) {
      body.locationId = locationId;
    }
    const response = await api.post('/timecards/sync-step1', body);
    return response.data;
  },
  
  checkSyncNeeded: async (employeeId: number, weekStartDate: string, locationId?: number): Promise<{ syncNeeded: boolean; reason: string }> => {
    const params: Record<string, number | string> = { employeeId, weekStartDate };
    if (locationId !== undefined && locationId !== null && locationId !== 0) {
      params.locationId = locationId;
    }
    const response = await api.get('/timecards/check-sync-needed', { params });
    return response.data;
  },

  approve: async (id: number): Promise<void> => {
    await api.post(`/timecards/${id}/approve`);
  },

  unapprove: async (id: number): Promise<void> => {
    await api.post(`/timecards/${id}/unapprove`);
  },
};

// Audit Log Config API
export const auditLogConfigApi = {
  getAll: async (): Promise<AuditLogConfig[]> => {
    const response = await api.get('/audit-log-configs');
    return response.data;
  },

  create: async (request: {
    entity: string;
    isActive: boolean;
    actions: string[];
    fields: AuditLogConfigFieldRequest[];
  }): Promise<{ id: number }> => {
    const response = await api.post('/audit-log-configs', request);
    return response.data;
  },

  update: async (id: number, request: {
    entity: string;
    isActive: boolean;
    actions: string[];
    fields: AuditLogConfigFieldRequest[];
  }): Promise<void> => {
    await api.put(`/audit-log-configs/${id}`, request);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/audit-log-configs/${id}`);
  },

  activate: async (id: number): Promise<void> => {
    await api.put(`/audit-log-configs/${id}/activate`);
  },

  deactivate: async (id: number): Promise<void> => {
    await api.put(`/audit-log-configs/${id}/deactivate`);
  },
};

// Audit Logs API
export const auditLogApi = {
  getAll: async (params?: {
    entity?: string;
    action?: string;
    fromDate?: string;
    toDate?: string;
    page?: number;
    pageSize?: number;
  }): Promise<{
    data: AuditLogEvent[];
    totalCount: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const response = await api.get('/audit-logs', { params });
    return response.data;
  },
};
